import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
} from '@mui/material'
import { formatNumber } from '../../utils/formatters'

const ProviderStatsCard = ({
  title,
  value,
  subtitle,
  icon,
  color = 'primary',
  loading = false,
  trend = null, // { value: number, isPositive: boolean }
}) => {
  
  const getColorValue = (color) => {
    const colorMap = {
      primary: 'primary.main',
      secondary: 'secondary.main',
      success: 'success.main',
      warning: 'warning.main',
      error: 'error.main',
      info: 'info.main',
    }
    return colorMap[color] || 'primary.main'
  }

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Skeleton variant="circular" width={48} height={48} />
          </Box>
          <Skeleton variant="text" width="60%" height={32} sx={{ mx: 'auto', mb: 1 }} />
          <Skeleton variant="text" width="40%" height={20} sx={{ mx: 'auto' }} />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card 
      sx={{ 
        height: '100%',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: 3,
        }
      }}
    >
      <CardContent sx={{ textAlign: 'center' }}>
        {/* Icon */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mb: 2,
            color: getColorValue(color)
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 48 } })}
        </Box>

        {/* Value */}
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            mb: 1,
            color: getColorValue(color)
          }}
        >
          {typeof value === 'number' ? formatNumber(value) : value}
        </Typography>

        {/* Title */}
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ mb: subtitle || trend ? 1 : 0 }}
        >
          {title}
        </Typography>

        {/* Subtitle */}
        {subtitle && (
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ display: 'block', mb: trend ? 1 : 0 }}
          >
            {subtitle}
          </Typography>
        )}

        {/* Trend */}
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 0,
                height: 0,
                borderLeft: '4px solid transparent',
                borderRight: '4px solid transparent',
                borderBottom: trend.isPositive ? '6px solid' : 'none',
                borderTop: !trend.isPositive ? '6px solid' : 'none',
                borderBottomColor: trend.isPositive ? 'success.main' : undefined,
                borderTopColor: !trend.isPositive ? 'error.main' : undefined,
              }}
            />
            <Typography 
              variant="caption" 
              sx={{ 
                color: trend.isPositive ? 'success.main' : 'error.main',
                fontWeight: 600
              }}
            >
              {Math.abs(trend.value)}%
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default ProviderStatsCard